import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Badge,
  Box,
  Button,
  Checkbox,
  Collapse,
  FormControlLabel,
  InputAdornment,
  ListItemIcon,
  ListItemText,
  MenuItem,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  ClearRounded,
  FilterListRounded,
  SearchRounded,
} from "@mui/icons-material";
import color from "../../../Constants/colors";
import {
  DOCUMENT_LANGUAGES,
  DocumentCraftingLanguage,
  DocumentCraftingListScope,
  DocumentCraftingListSort,
  DocumentCraftingType,
  DocumentCraftingUser,
} from "../../../Models/documentCrafting";
import { useDocumentCraftingTypes } from "../../../Query/useDocumentCrafting";
import { DocumentTypeIcon } from "../Common/DocumentCraftingUi";
import { useDocumentCraftingAccess } from "../Common/documentCraftingNavigation";
import {
  TYPE_KEY_BY_ID,
  useDocumentCraftingEnumLabels,
} from "../Common/useDocumentCraftingEnumLabels";
import LabelFilter from "./LabelFilter";
import MemberFilter from "./MemberFilter";
import { useDebounced } from "./useDebounced";
import {
  DOCUMENT_LIST_SCOPES,
  DOCUMENT_LIST_SORTS,
  DocumentListParams,
  SortOrder,
} from "./useDocumentListParams";

// Phase 1 and 2 types, until GET /types/ says which are enabled.
const FALLBACK_TYPES = [
  DocumentCraftingType.PRESENTATION,
  DocumentCraftingType.ONEPAGER_PARTNER,
  DocumentCraftingType.ONEPAGER_SERVICE,
  DocumentCraftingType.FLYER,
  DocumentCraftingType.CASE_STUDY,
  DocumentCraftingType.INVITATION,
  DocumentCraftingType.CHECKLIST_GUIDE,
];

const SORT_OPTIONS = DOCUMENT_LIST_SORTS.reduce<
  { value: string; sort: DocumentCraftingListSort; order: SortOrder }[]
>((list, sort) => {
  const orders: SortOrder[] =
    sort === "title" || sort === "type" ? ["asc", "desc"] : ["desc", "asc"];
  orders.forEach((order) => list.push({ value: `${sort}:${order}`, sort, order }));
  return list;
}, []);

const SEARCH_DELAY_MS = 300;

/** Search, scope, filters and sort of the document list (all in the URL). */
const DocumentFilters: React.FC<{
  params: DocumentListParams;
  knownUsers: (DocumentCraftingUser | null)[];
}> = ({ params, knownUsers }) => {
  const { t } = useTranslation("documentCrafting");
  const labels = useDocumentCraftingEnumLabels();
  const theme = useTheme();
  const isNarrow = useMediaQuery(theme.breakpoints.down("md"), { noSsr: true });
  const { state, activeFilterCount, setSearch } = params;
  const types = useDocumentCraftingTypes();
  const { hasWorkspace } = useDocumentCraftingAccess();

  // The field shows what is typed; the URL gets it 300 ms later. A change of
  // `q` from outside (clear filters, Back) replaces the typed text.
  const [searchInput, setSearchInput] = useState(state.q);
  const [seenQ, setSeenQ] = useState(state.q);
  const [sentQ, setSentQ] = useState(state.q);
  if (seenQ !== state.q) {
    setSeenQ(state.q);
    if (state.q !== sentQ) {
      setSearchInput(state.q);
      setSentQ(state.q);
    }
  }
  const updateSearch = useDebounced((value: string) => {
    setSentQ(value.trim());
    setSearch(value);
  }, SEARCH_DELAY_MS);

  const [filtersOpen, setFiltersOpen] = useState(activeFilterCount > 0);

  const typeOptions = useMemo(
    () =>
      types.data
        ? types.data.types.filter((type) => type.enabled).map((type) => type.id)
        : FALLBACK_TYPES,
    [types.data],
  );

  const hasAnything = activeFilterCount > 0 || state.q !== "" || searchInput !== "";
  const clearAll = () => {
    setSearchInput("");
    setSentQ("");
    params.clearFilters();
  };

  const filters = (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "minmax(0, 1fr)",
          sm: "repeat(2, minmax(0, 1fr))",
          md: "repeat(4, minmax(0, 1fr))",
        },
        gap: 1.5,
        pt: 1.5,
      }}
    >
      <TextField
        select
        size="small"
        label={t("list.filters.type")}
        value={state.types}
        InputLabelProps={{ shrink: true }}
        SelectProps={{
          multiple: true,
          displayEmpty: true,
          renderValue: (selected) => {
            const list = selected as DocumentCraftingType[];
            if (!list.length) {
              return (
                <Box component="span" sx={{ color: color.gray500 }}>
                  {t("list.filters.allTypes")}
                </Box>
              );
            }
            return list.length === 1
              ? labels.typeById(list[0])
              : t("list.filters.typesSelected", { count: list.length });
          },
        }}
        onChange={(event) => {
          const value = event.target.value as unknown;
          params.setTypes(
            (Array.isArray(value) ? value : String(value).split(","))
              .map(Number)
              .filter((type) => !Number.isNaN(type)) as DocumentCraftingType[],
          );
        }}
      >
        {typeOptions.map((type) => (
          <MenuItem key={type} value={type} dense>
            <Checkbox
              size="small"
              checked={state.types.indexOf(type) !== -1}
              sx={{ p: 0.5, mr: 1 }}
            />
            <ListItemIcon sx={{ minWidth: 28 }}>
              <DocumentTypeIcon
                typeKey={TYPE_KEY_BY_ID[type]}
                size={18}
                tooltip={false}
              />
            </ListItemIcon>
            <ListItemText
              primary={labels.typeById(type)}
              primaryTypographyProps={{ fontSize: 14, color: color.gray900 }}
            />
          </MenuItem>
        ))}
      </TextField>

      <LabelFilter
        projectLabelIds={state.labels}
        personalLabelIds={state.personalLabels}
        onChange={params.setLabels}
      />

      <TextField
        select
        size="small"
        label={t("list.filters.language")}
        value={state.language ?? ""}
        InputLabelProps={{ shrink: true }}
        SelectProps={{ displayEmpty: true }}
        onChange={(event) =>
          params.setLanguage(
            (event.target.value || null) as DocumentCraftingLanguage | null,
          )
        }
      >
        <MenuItem value="">{t("list.filters.allLanguages")}</MenuItem>
        {DOCUMENT_LANGUAGES.map((language) => (
          <MenuItem key={language} value={language}>
            {labels.language(language)}
          </MenuItem>
        ))}
      </TextField>

      {hasWorkspace && (
        <MemberFilter
          value={state.createdBy}
          onChange={params.setCreatedBy}
          knownUsers={knownUsers}
        />
      )}

      <TextField
        type="date"
        size="small"
        label={t("list.filters.from")}
        value={state.from ?? ""}
        InputLabelProps={{ shrink: true }}
        inputProps={{ max: state.to ?? undefined }}
        onChange={(event) => params.setFrom(event.target.value || null)}
      />
      <TextField
        type="date"
        size="small"
        label={t("list.filters.to")}
        value={state.to ?? ""}
        InputLabelProps={{ shrink: true }}
        inputProps={{ min: state.from ?? undefined }}
        onChange={(event) => params.setTo(event.target.value || null)}
      />

      <TextField
        select
        size="small"
        label={t("list.filters.sort")}
        value={`${state.sort}:${state.order}`}
        onChange={(event) => {
          const option = SORT_OPTIONS.find(
            (item) => item.value === event.target.value,
          );
          if (option) params.setSort(option.sort, option.order);
        }}
      >
        {SORT_OPTIONS.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {t(`list.sortOptions.${option.sort}_${option.order}`)}
          </MenuItem>
        ))}
      </TextField>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          minHeight: 40,
        }}
      >
        <FormControlLabel
          sx={{ ml: 0, mr: 0, "& .MuiFormControlLabel-label": { fontSize: 14 } }}
          control={
            <Checkbox
              size="small"
              checked={state.hasDraft}
              onChange={(event) => params.setHasDraft(event.target.checked)}
            />
          }
          label={t("list.filters.hasDraft")}
        />
        {hasAnything && (
          <Button
            size="small"
            startIcon={<ClearRounded />}
            onClick={clearAll}
            sx={{ color: color.gray700, flexShrink: 0 }}
          >
            {t("list.filters.clear")}
          </Button>
        )}
      </Box>
    </Box>
  );

  return (
    <Box sx={{ mb: 2 }}>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 1.5,
        }}
      >
        <TextField
          size="small"
          value={searchInput}
          placeholder={t("list.search.placeholder")}
          onChange={(event) => {
            setSearchInput(event.target.value);
            updateSearch(event.target.value);
          }}
          sx={{ flex: "1 1 280px", minWidth: 0 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchRounded sx={{ color: color.gray400 }} />
              </InputAdornment>
            ),
          }}
          inputProps={{ "aria-label": t("list.search.label") }}
        />
        {/* Without a workspace every document is the user's own (personal). */}
        {(hasWorkspace || state.scope !== "all") && (
          <ToggleButtonGroup
            size="small"
            exclusive
            value={state.scope}
            onChange={(_, value: DocumentCraftingListScope | null) =>
              value && params.setScope(value)
            }
            aria-label={t("list.scope.label")}
            sx={{ flexShrink: 0, "& .MuiToggleButton-root": { px: 1.5 } }}
          >
            {DOCUMENT_LIST_SCOPES.map((scope) => (
              <ToggleButton key={scope} value={scope}>
                {t(`list.scope.${scope}`)}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        )}
        {isNarrow && (
          <Button
            variant="outlined"
            onClick={() => setFiltersOpen((open) => !open)}
            aria-expanded={filtersOpen}
            startIcon={
              <Badge
                color="primary"
                badgeContent={activeFilterCount}
                invisible={!activeFilterCount}
              >
                <FilterListRounded />
              </Badge>
            }
          >
            {t("list.filters.toggle")}
          </Button>
        )}
      </Box>
      {isNarrow ? <Collapse in={filtersOpen}>{filters}</Collapse> : filters}
    </Box>
  );
};

export default DocumentFilters;
